import { z } from "zod";
import { userProfileSchema } from "./user";
import { levelProgressSchema, streakSummarySchema } from "./progression";

export const playerStatsSchema = z.object({
  gamesPlayed: z.number().int(),
  gamesWon: z.number().int(),
  totalWagered: z.number().int(),
  totalPayout: z.number().int(),
  largestWin: z.number().int(),
});

export type PlayerStatsDTO = z.infer<typeof playerStatsSchema>;

export const identitySettingsSchema = z.object({
  /** When true, other players see the real Telegram name/photo below instead of the account's anonymous public name/avatar. Defaults to false. */
  revealIdentity: z.boolean(),
  /** The account's real Telegram first/last name, refreshed on every login. Only shown to OTHER players when revealIdentity is true. */
  telegramDisplayName: z.string().nullable(),
});

export type IdentitySettingsDTO = z.infer<typeof identitySettingsSchema>;

/** The current user's own full profile — includes wallet balance, never sent for other players. */
export const playerProfileSchema = z.object({
  profile: userProfileSchema,
  identity: identitySettingsSchema,
  wallet: z.object({ balance: z.number().int() }),
  progression: levelProgressSchema,
  streak: streakSummarySchema,
  achievementsUnlockedCount: z.number().int(),
  stats: playerStatsSchema,
});

export type PlayerProfileDTO = z.infer<typeof playerProfileSchema>;

export const updateIdentityVisibilityRequestSchema = z.object({
  revealIdentity: z.boolean(),
});

export type UpdateIdentityVisibilityRequest = z.infer<typeof updateIdentityVisibilityRequestSchema>;

/**
 * A lightweight, privacy-conscious summary suitable for viewing ANOTHER
 * player (e.g. tapping their name in chat) — no wallet balance, no
 * Telegram id, no stats beyond what's meant to be public.
 */
export const publicPlayerSummarySchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  level: z.number().int(),
  memberSince: z.string(),
  achievementsUnlockedCount: z.number().int(),
});

export type PublicPlayerSummaryDTO = z.infer<typeof publicPlayerSummarySchema>;
